/**
 * TreatX Messaging API Service - Omnichannel Ready (S1: Auth Hardened)
 * Handles communication with the v2 messaging backend
 * All requests include JWT auth token.
 */
import api from '../utils/api';


/**
 * Get auth headers — tries unified token first, then patient token.
 */
function _authHeaders() {
  const token =
    '' ||
    localStorage.getItem('treatx_agent_token') ||
    localStorage.getItem('clinicToken');
  if (token) {
    return {};
  }
  return {};
}

/**
 * Thread Types
 */
export const THREAD_TYPES = {
  LEAD: 'lead',
  OFFER: 'offer',
  CLINIC: 'clinic',
  ESCALATION: 'escalation'
};

/**
 * Message Channels (Omnichannel Ready)
 */
export const MESSAGE_CHANNELS = {
  TREATX_NATIVE: 'treatx_native',
  WHATSAPP: 'whatsapp',      // Phase C
  INSTAGRAM: 'instagram',    // Phase C
  EMAIL: 'email',            // Phase C
  SMS: 'sms'                 // Phase C
};

/**
 * Message Direction
 */
export const MESSAGE_DIRECTION = {
  INBOUND: 'inbound',
  OUTBOUND: 'outbound',
  INTERNAL: 'internal'
};

/**
 * Delivery Status
 */
export const DELIVERY_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read',
  FAILED: 'failed'
};

/**
 * Participant Roles
 */
export const PARTICIPANT_ROLES = {
  PATIENT: 'patient',
  SALES_REP: 'provider_sales_rep',
  CLINIC_ADMIN: 'provider_admin',
  TREATX_ADMIN: 'treatx_admin',
  COORDINATOR: 'coordinator',
  SYSTEM: 'system'
};

/**
 * Message Types
 */
export const MESSAGE_TYPES = {
  TEXT: 'text',
  FILE: 'file',
  IMAGE: 'image',
  OFFER_REF: 'offer_ref',
  SYSTEM: 'system'
};

/**
 * Messaging Events (for push notification handling)
 */
export const MESSAGING_EVENTS = {
  MESSAGE_CREATED: 'message_created',
  MESSAGE_READ: 'message_read',
  MESSAGE_DELIVERED: 'message_delivered',
  THREAD_CREATED: 'thread_created',
  THREAD_ESCALATED: 'thread_escalated',
  THREAD_RESOLVED: 'thread_resolved',
  OFFER_ATTACHED: 'offer_attached',
  FILE_ATTACHED: 'file_attached'
};

/**
 * Messaging API Service
 */
const MessagingAPI = {
  /**
   * Create a new thread
   * @param {Object} params
   * @param {string} params.threadType - Thread type (lead, offer, clinic, escalation)
   * @param {string} [params.contextId] - Context ID (lead_id, offer_id)
   * @param {string} [params.contextType] - Context type
   * @param {string} [params.clinicId] - Clinic ID
   * @param {string} [params.subject] - Thread subject
   * @param {string} [params.initialMessage] - Initial message content
   * @param {string} [params.channel='treatx_native'] - Message channel
   */
  async createThread({ threadType, contextId, contextType, clinicId, subject, initialMessage, channel = 'treatx_native' }) {
    const response = await api.post('/v2/threads', {
      thread_type: threadType,
      context_id: contextId,
      context_type: contextType,
      clinic_id: clinicId,
      subject,
      initial_message: initialMessage,
      channel,
    });
    
    return response.data;
  },

  /**
   * Get threads for a user (mobile sync compatible)
   * @param {Object} params
   * @param {string} params.userId - User ID
   * @param {string} [params.threadType] - Filter by thread type
   * @param {string} [params.status] - Filter by status
   * @param {string} [params.channel] - Filter by channel
   * @param {number} [params.limit=50] - Results limit
   * @param {number} [params.offset=0] - Results offset
   * @param {string} [params.sinceTimestamp] - For incremental sync
   * @returns {Promise<{threads: Array, total: number, unread_count: number, sync_token: string}>}
   */
  async getUserThreads({ userId, threadType, status, channel, limit = 50, offset = 0, sinceTimestamp }) {
    const params = new URLSearchParams({ user_id: userId, limit, offset });
    if (threadType) params.append('thread_type', threadType);
    if (status) params.append('status', status);
    if (channel) params.append('channel', channel);
    if (sinceTimestamp) params.append('since_timestamp', sinceTimestamp);
    
    const response = await api.get(`/v2/threads?${params}`);
    
    return response.data;
  },

  /**
   * Get threads for a clinic
   */
  async getClinicThreads({ clinicId, threadType, limit = 50, offset = 0 }) {
    const params = new URLSearchParams({ limit, offset });
    if (threadType) params.append('thread_type', threadType);
    
    const response = await api.get(`/v2/threads/clinic/${clinicId}?${params}`);
    
    return response.data;
  },

  /**
   * Get all threads (admin)
   */
  async getAllThreadsAdmin({ threadType, status, clinicId, limit = 50, offset = 0 }) {
    const params = new URLSearchParams({ limit, offset });
    if (threadType) params.append('thread_type', threadType);
    if (status) params.append('status', status);
    if (clinicId) params.append('clinic_id', clinicId);
    
    const response = await api.get(`/v2/threads/admin?${params}`);
    
    return response.data;
  },

  /**
   * Get a specific thread
   */
  async getThread(threadId) {
    const response = await api.get(`/v2/threads/${threadId}`);
    
    return response.data;
  },

  /**
   * Get messages for a thread
   */
  async getMessages({ threadId, limit = 100, beforeId }) {
    const params = new URLSearchParams({ limit });
    if (beforeId) params.append('before_id', beforeId);
    
    const response = await api.get(`/v2/threads/${threadId}/messages?${params}`);
    
    return response.data;
  },

  /**
   * Send a message to a thread
   */
  async sendMessage({ threadId, senderId, senderRole, senderName, content, messageType = 'text', attachments = [], offerReference = null }) {
    const response = await api.post(`/v2/threads/${threadId}/messages`, {
      thread_id: threadId,
      sender_id: senderId,
      sender_role: senderRole,
      sender_name: senderName,
      content,
      message_type: messageType,
      attachments,
      offer_reference: offerReference,
    });
    return response.data;
  },

  /**
   * Mark messages as read
   *
   * B-2 / INV-M10 — read-state actor is derived from the JWT server-
   * side. We no longer send `user_id` in the URL: the parameter is
   * ignored on the wire and would be rejected as a mismatched
   * identity by the server if a mismatched value were ever supplied.
   * `userId` is still accepted for callsite ergonomics but never
   * transmitted.
   */
  async markMessagesRead({ threadId, userId: _userId }) {
    const response = await api.post(
      `/v2/messages/mark-read?thread_id=${threadId}`
    );
    return response.data;
  },

  /**
   * Get unread count for a user
   */
  async getUnreadCount(userId) {
    try {
      const response = await api.get(`/v2/messages/unread/${userId}`);
      return response.data;
    } catch {
      return { unread_count: 0 };
    }
  },

  /**
   * Escalate a thread
   */
  async escalateThread({ threadId, adminId, reason }) {
    const params = new URLSearchParams({ admin_id: adminId, reason });
    const response = await api.post(`/v2/threads/${threadId}/escalate?${params}`);
    return response.data;
  },

  /**
   * Resolve a thread
   */
  async resolveThread(threadId) {
    const response = await api.post(`/v2/threads/${threadId}/resolve`);
    return response.data;
  },

  /**
   * Add participant to a thread
   */
  async addParticipant({ threadId, userId, role, name, clinicId }) {
    const params = new URLSearchParams({ user_id: userId, role, name });
    if (clinicId) params.append('clinic_id', clinicId);
    const response = await api.post(`/v2/threads/${threadId}/participants?${params}`);
    return response.data;
  },

  /**
   * Get risk summary for a thread (guard layer)
   */
  async getThreadRisk(threadId) {
    try {
      const response = await api.get(`/v2/guard/thread/${threadId}/risk`);
      return response.data;
    } catch {
      return null;
    }
  },

  /**
   * Get flagged threads (guard layer)
   */
  async getFlaggedThreads({ clinicId, limit = 50, offset = 0 } = {}) {
    try {
      const params = new URLSearchParams({ limit, offset });
      if (clinicId) params.append('clinic_id', clinicId);
      const response = await api.get(`/v2/guard/flagged-threads?${params}`);
      return response.data;
    } catch {
      return { threads: [], total: 0 };
    }
  },

  /**
   * Get communication policy for a thread
   */
  async getThreadPolicy(threadId) {
    try {
      const response = await api.get(`/v2/threads/${threadId}/communication-policy`);
      return response.data;
    } catch {
      return null;
    }
  }
};

export default MessagingAPI;
